import NextImage from 'next/image';

import logo from '../assets/logo.png';
import CTABackgroundGreyMobile from '../assets/FishingReportCTABackground-grey-mobile.png';
import CTABackgroundGreyTablet from '../assets/FishingReportCTABackground-grey-tablet.png';
import CTABackgroundGreyDesktop from '../assets/FishingReportCTABackground-grey-desktop.png';
import CTABackgroundMobile from '../assets/FishingReportCTABackground-mobile.png';
import CTABackgroundTablet from '../assets/FishingReportCTABackground-tablet.png';
import CTABackgroundDesktop from '../assets/FishingReportCTABackground.png';
import ArrowWhite from '../assets/arrow-white.svg';
import ArrowBlack from '../assets/arrow-black.svg';
import { Source } from '../../../components/NextImage/Source';
import { AppStoreButtons } from '../../../components/Clickables/Buttons/AppStoreButtons/AppStoreButtons';
import { Heading } from '../../../components/Heading/Heading';
import { Text } from '../../../components/Text/Text';
import { QRCode } from '../../../components/QRCode/QRCode';
import { Rating } from '../../../components/Rating/Rating';
import { RATING_ICON_WIDTH, RatingVariant } from '../../../components/Rating/constants/constants';
import {
  ANDROID_RATING_COUNT,
  AVERAGE_APP_RATING,
  IOS_ALL_RATING_COUNT,
  IOS_APP_RATING,
} from '@config/constants';
import { useClientHasRendered } from '@domain/session/useClientHasRendered';
import { useIsAndroid } from '@hooks/useIsAndroid';
import { AppRatingLoader } from '../../../components/AppRatingSection/components/AppRatingLoader';

const TOO_FEW_CATCHES = 4;
interface IProps {
  catchCount: number;
}

export const FishingReportCTASection = ({ catchCount }: IProps) => {
  const isAndroid = useIsAndroid();
  const clientHasRendered = useClientHasRendered();
  const hasTooFewCatches = catchCount < TOO_FEW_CATCHES;

  const backGroundMobile = hasTooFewCatches ? CTABackgroundGreyMobile : CTABackgroundMobile;
  const backGroundTablet = hasTooFewCatches ? CTABackgroundGreyTablet : CTABackgroundTablet;
  const backGroundDesktop = hasTooFewCatches ? CTABackgroundGreyDesktop : CTABackgroundDesktop;
  const arrow = hasTooFewCatches ? ArrowBlack : ArrowWhite;

  return (
    <div
      className={`relative ${hasTooFewCatches ? 'pt-2 sm:pt-4 md:pt-8' : 'mt-2 pt-6 sm:mt-4 sm:pt-10 lg:py-12'}`}
    >
      {/* Faded out cards overlay */}
      {!hasTooFewCatches && (
        <div className="pointer-events-none absolute inset-0 grid grid-cols-4 gap-2 sm:grid-cols-12 sm:gap-4">
          <div className="from-lake-fog relative col-span-2 flex flex-col rounded-xl bg-linear-to-b pb-[115%] sm:col-span-4 md:col-span-3"></div>
          <div className="from-lake-fog relative col-span-2 flex flex-col rounded-xl bg-linear-to-b pb-[115%] sm:col-span-4 md:col-span-3"></div>
          <div className="from-lake-fog relative col-span-2 hidden flex-col rounded-xl bg-linear-to-b pb-[115%] sm:col-span-4 sm:flex md:col-span-3"></div>
          <div className="from-lake-fog relative col-span-2 hidden flex-col rounded-xl bg-linear-to-b pb-[115%] sm:col-span-4 md:col-span-3 md:flex"></div>
        </div>
      )}
      {/* CTA Banner */}
      <div
        className={`${hasTooFewCatches ? 'mx-0' : 'mx-6 sm:mx-10'} relative max-w-(--breakpoint-lg) rounded-xl p-6 sm:p-8 lg:mx-28 xl:mx-auto xl:w-full`}
      >
        <picture>
          <Source media="(min-width: 990px)" src={backGroundDesktop}></Source>
          <Source media="(min-width: 600px)" src={backGroundTablet}></Source>
          <NextImage
            data-testid="appBannerLogo"
            alt="Fishbrain"
            src={backGroundMobile}
            fill
            className="rounded-xl"
          />
        </picture>
        <div className="flex flex-col items-center justify-between gap-4 sm:flex-row">
          <div className="flex flex-row items-center sm:max-w-[50%] md:max-w-none md:gap-x-10">
            <NextImage
              data-testid="appBannerLogo"
              alt="Fishbrain"
              src={logo}
              unoptimized
              className="z-mid hidden max-h-24 justify-start md:flex"
            />
            <div className="md:max-w-auto z-mid text-sea-foam flex flex-col gap-2 text-center sm:text-left">
              {hasTooFewCatches ? (
                <>
                  <Heading as="div" className="text-trench" variant="xs">
                    Have you been fishing here?
                  </Heading>
                  <Text className="text-trench m-0" variant="sm">
                    Log your catch and check out other catches from the community in the Fishbrain
                    app.
                  </Text>
                </>
              ) : (
                <>
                  <Heading as="div" className="text-sea-foam" variant="xs">
                    More catches in the app...
                  </Heading>
                  <Text className="m-0" variant="sm">
                    Continue browsing catches and catch locations in the Fishbrain app
                  </Text>
                </>
              )}
            </div>
          </div>

          <div className="flex flex-col items-center justify-center gap-4">
            <div className={`z-mid ${hasTooFewCatches ? 'text-trench' : 'text-sea-foam'}`}>
              <Rating
                rating={isAndroid ? AVERAGE_APP_RATING : IOS_APP_RATING}
                variant={RatingVariant.allStars}
                textSize="sm"
                isLarge
                iconSize={RATING_ICON_WIDTH}
                reviewPointColor={hasTooFewCatches ? 'text-trench' : 'text-sea-foam'}
              />
              {clientHasRendered ? (
                <Text
                  variant="sm"
                  as="span"
                  className={`ml-1 font-semibold ${hasTooFewCatches ? 'text-trench' : 'text-sea-foam'}`}
                >
                  {isAndroid
                    ? `${ANDROID_RATING_COUNT} app store ratings`
                    : `${IOS_ALL_RATING_COUNT} ratings`}
                </Text>
              ) : (
                <AppRatingLoader key="fishingreportCTA-apprating-key" />
              )}
            </div>

            <AppStoreButtons
              className="z-mid md:hidden"
              orientation="vertical"
              source="FishingReportCTASection"
            />
          </div>

          <div className="z-mid relative ml-4 hidden items-center justify-center sm:justify-start md:flex">
            <div className="relative pb-2">
              <Text
                className={`m-0 -rotate-12 whitespace-pre-line ${hasTooFewCatches ? 'text-trench' : 'text-sea-foam'}`}
                variant="sm"
              >
                {`Scan the QR code 
                to download the app!`}
              </Text>
              <NextImage
                alt="Arrow pointing to QR code"
                src={arrow}
                className="absolute right-0 bottom-0 translate-y-full"
                unoptimized
              />
            </div>

            <QRCode
              className="bg-sea-foam rounded-md p-2 md:ml-4"
              source="FishingReportCTASection"
            />
          </div>
        </div>
      </div>
    </div>
  );
};
